/**
 * `Location.kind` — PIP-87's venue-kind discriminator, and the ONE rule that
 * derives it (API-1717).
 *
 * `kind` is read across the codebase as COR-25's verdict on the row: Discover
 * and `topPicks` drop every event at a non-`resolved` venue, while admin shows
 * the same rows as `nameIsAddress`. PIP-289 made the classifier DELEGATE to
 * COR-25 so the two agree when a row is written. API-1717 moved it here, next
 * to COR-25, because a writer outside data-pipeline — the admin venue PATCH —
 * has to re-derive `kind` too, and backend-api cannot import from
 * data-pipeline. A second copy of the rule in backend-api is exactly the
 * drift PIP-289 removed, so there is none: every writer calls this module.
 *
 * COR-124 added the second verdict the same writers need: a name that is a
 * placeholder rather than a venue (`TBA`, `Online`, `Charlotte, NC`). It lives
 * here, beside the address verdict it mirrors, for the same reason.
 *
 * ## COR-124 shipped in two releases (expand, then contract)
 *
 * COR-124's release EXPANDED: the `unresolved-placeholder` enum value, every
 * reader that understands it, and {@link isPlaceholderVenueName}, which the
 * public venue index applies by name (`placeholderVenueExclusionSql` in
 * db-core). No writer stored the new value, because the previous release's
 * Prisma client throws (`Value 'unresolved-placeholder' not found in enum
 * 'VenueKind'`) on any row carrying it. That client keeps serving while the
 * new container runs `prisma migrate deploy` and drains, and it is what a
 * rollback returns to (docs/deployment/branching-and-deployment-strategy.md,
 * "Expand/contract, always").
 *
 * COR-127 is the CONTRACT step, gated to ship in a release after COR-124's is
 * live in production: {@link classifyVenueKind} returns the new kind, and
 * `20260927200000_cor127_condemn_placeholder_venue_kind` re-tags the stored
 * rows.
 */
import { CITY_RECORDS } from '@loci/city-editions';
import {
  VENUE_KIND_PRISMA_IDENTIFIERS,
  type DiscoverVenueKind,
  type VenueKindPrismaIdentifier,
} from '@loci/types';
import { isAddressLikeVenueName } from './scoreEventForPlacement';

/**
 * The `VenueKind` values as Postgres stores them — hyphenated, via the
 * schema's `@map` annotations (`packages/db-core/prisma/schema.prisma`). Raw
 * SQL reads return this spelling. It is the same union `@loci/types` puts on
 * the wire as `DiscoverVenueKind`, whose `venueKindFromPrismaIdentifier` is the
 * inverse of {@link venueKindToPrisma}.
 */
export type VenueKindLabel = DiscoverVenueKind;

/**
 * The same values as the Prisma client spells them. TypeScript identifiers
 * cannot contain hyphens, so Prisma rewrites `unresolved-address` to
 * `unresolved_address`; model-API reads return this spelling and model-API
 * writes require it. Derived from `@loci/types`' one mapping.
 */
export type VenueKindPrismaValue = VenueKindPrismaIdentifier;

/**
 * COR-124 — the labels feeds put in the venue slot when there is no venue,
 * spelled as {@link normalizeVenueLabel} returns them.
 *
 * Every entry was measured as a `Location.name` on the Neon `production` branch
 * (2026-09-26), is a fallback LOCI's own writers mint when a feed has no venue
 * (`Unknown Venue`: the Ticketmaster, Eventbrite and Charlotte Regional
 * transformers; `Unknown`: the enrichment and dedup jobs), or is the obvious
 * sibling of one (`tbd` beside `tba`). The list is CLOSED and matched
 * exactly, never as a substring. `Charlotte Online Events` is a placeholder,
 * while `Online Trivia Night at Petra's` is a venue that happens to contain
 * the word. A wrongly kept row stays visible and gets fixed, but a wrongly
 * hidden one leaves every public surface with nothing to prompt the fix.
 * Widen the list only from evidence (and read {@link PLACEHOLDER_VENUE_NAMES}
 * on the re-tag migration a widening needs, now that stored rows carry the
 * verdict).
 *
 * City names are NOT here. They are per-city facts, so
 * {@link PLACEHOLDER_VENUE_NAMES} derives them from `CITY_RECORDS`.
 */
export const PLACEHOLDER_VENUE_LABELS: readonly string[] = [
  // Not announced yet.
  'tba',
  'tbd',
  'to be announced',
  'to be determined',
  'venue tba',
  'venue tbd',
  'location tba',
  'location tbd',
  'location tbd register online',
  'location provided after booking',
  'private location',
  // LOCI's own no-venue fallbacks.
  'unknown',
  'unknown venue',
  // Not a place.
  'online',
  'online event',
  'online events',
  'virtual',
  'virtual event',
  // An Eventbrite organizer's recurring series title, shipped as its venue.
  'restaurant of the week',
  'restaurant of the month',
];

/**
 * COR-124 — the separator class {@link normalizeVenueLabel} collapses to one
 * space, as a regex SOURCE so db-core's `regexp_replace` binds the very same
 * string. ASCII only, and no case flag: see {@link normalizeVenueLabel}.
 */
export const VENUE_LABEL_SEPARATOR_PATTERN = '[^a-zA-Z0-9]+';

const VENUE_LABEL_SEPARATOR = new RegExp(VENUE_LABEL_SEPARATOR_PATTERN, 'g');

/**
 * COR-124 — fold a venue name into the form {@link PLACEHOLDER_VENUE_NAMES}
 * holds. Every run of characters outside `[a-zA-Z0-9]` becomes one space, the
 * ends are trimmed, and the result is lowercased.
 * `"Charlotte, N.C."` becomes `charlotte n c` and `"Location TBD; Register
 * Online"` becomes `location tbd register online`.
 *
 * The SQL mirror (`placeholderVenueExclusionSql` in db-core) is
 * `lower(btrim(regexp_replace(name, <VENUE_LABEL_SEPARATOR_PATTERN>, ' ', 'g')))`.
 * The two spellings are kept equivalent on purpose:
 *
 *   - the class is ASCII and has no `i` flag, so neither engine's case folding
 *     or locale widens it; a non-ASCII letter (`é`) is a separator in both;
 *   - it replaces BEFORE lowercasing, so the only characters left to lowercase
 *     are ASCII, where JS `toLowerCase()` and Postgres `lower()` agree;
 *   - after the replace, only ASCII spaces can sit at either end, so JS
 *     `.trim()` and Postgres's default `btrim` remove the same characters.
 */
export function normalizeVenueLabel(name: string): string {
  return name.replace(VENUE_LABEL_SEPARATOR, ' ').trim().toLowerCase();
}

/**
 * COR-124 — every normalized name that is a placeholder: the labels, each
 * recognised city on its own (`charlotte`, `charlotte nc`, `charlotte n c`),
 * and each label prefixed by a city name (`charlotte venue tba`,
 * `charlotte online events`).
 *
 * City forms come from `CITY_RECORDS` (`locationCity` + `stateCode`), so adding
 * a market needs no edit here. `n c` is the dotted form feeds write
 * (`Charlotte, N.C.`). The set covers every recognised city, not just the row's
 * own: a Charlotte row named `Miami` is still not a venue, and matching without
 * the row's city keeps the SQL mirror a single `= ANY(…)`.
 *
 * db-core binds this set as a parameter, so the public filter always follows
 * it. Stored `kind`s do not: since COR-127, rows carry the verdict, and a row
 * keeps it until a writer touches it again. Adding a label or a city needs
 * its own re-tag migration, just as widening `STREET_TYPE_TOKENS` does, and
 * `cor127-condemn-placeholder-venue-kind.test.ts` in db-core fails until that
 * migration's snapshot matches this set.
 */
export const PLACEHOLDER_VENUE_NAMES: ReadonlySet<string> = (() => {
  const names = new Set<string>(PLACEHOLDER_VENUE_LABELS);
  for (const record of Object.values(CITY_RECORDS)) {
    const city = normalizeVenueLabel(record.locationCity);
    const state = record.stateCode.toLowerCase();
    names.add(city);
    names.add(`${city} ${state}`);
    names.add(`${city} ${state.split('').join(' ')}`);
    for (const label of PLACEHOLDER_VENUE_LABELS) names.add(`${city} ${label}`);
  }
  return names;
})();

/**
 * COR-124 — `true` when `name` is a placeholder rather than a venue:
 * `TBA`, `Online`, `Charlotte, N.C.`, `Restaurant of the Week`.
 *
 * The sibling of COR-25's {@link isAddressLikeVenueName}. Both answer "is this
 * `Location.name` really a venue?". The difference is that a placeholder
 * carries no address to corroborate, so this verdict reads the name alone. An
 * `address` column that names a street does not turn `TBA` into a venue.
 *
 * {@link classifyVenueKind} consults it after the address verdict (COR-127),
 * so a writer stores `unresolved-placeholder` for these names.
 */
export function isPlaceholderVenueName(name: string): boolean {
  return PLACEHOLDER_VENUE_NAMES.has(normalizeVenueLabel(name));
}

/**
 * PIP-87 — classify a venue name into the typed `Location.kind` discriminator.
 *
 * Returns `'unresolved-address'` when the supplied `name` is really a raw
 * postal address, `'unresolved-placeholder'` when it is a stand-in rather than
 * a venue ({@link isPlaceholderVenueName}: `TBA`, `Online`, `Charlotte, NC`;
 * COR-127), `'unresolved-brand'` when the caller passes
 * `{ unresolvedBrand: true }` — used by transformers that detected the upstream
 * feed shipped a brand/franchise label (e.g. `"Jazzercise"`) that didn't
 * resolve to a known venue record — and `'resolved'` otherwise.
 *
 * ## PIP-289 — the address verdict is COR-25's, not a second opinion
 *
 * PIP-87 shipped its own baseline here: `/^\s*\d{1,5}\s+\S/`, "leading 1-5
 * digits, whitespace, any non-space". That is strictly LOOSER than COR-25's
 * {@link isAddressLikeVenueName}, which additionally requires a street-type
 * token (`rd`, `ave`, `blvd`, …) or a `#`-prefixed unit marker, plus
 * address-column corroboration. The gap was not academic — it condemned real
 * Charlotte venues whose names simply start with a number:
 *
 * | name                    | PIP-87 baseline | COR-25 (authoritative) |
 * | ----------------------- | --------------- | ---------------------- |
 * | `100 W Worthington Ave` | address         | address                |
 * | `1263 Arrow Pine Dr`    | address         | address                |
 * | `300 East`              | **address**     | **NOT an address**     |
 * | `5 Church`              | **address**     | **NOT an address**     |
 * | `1900 Mexican Grill`    | **address**     | **NOT an address**     |
 * | `1904 Music Hall`       | **address**     | **NOT an address**     |
 *
 * `300 East` (Dilworth) and `5 Church` (Uptown) are real venues, and every
 * consumer of `kind` reads it as "this name is really a street address":
 * `topPicks` (`EXCLUDE_UNRESOLVED_VENUES`) and Discover both drop every event
 * at a non-`resolved` venue, so those venues could never reach a TOP PICK slot
 * or the Discover feed. Silently: `AdminVenuePayload` exposes COR-25's
 * `nameIsAddress`, which is `false` for these rows, so no operator was ever
 * prompted that anything was wrong with them.
 *
 * So this function holds no opinion. It DELEGATES to COR-25, which makes
 * `kind` and `nameIsAddress` the same verdict — the identity every consumer
 * already assumed. Do NOT reintroduce a local pattern here: a second spelling
 * of "is this an address" is exactly the drift PIP-289 removed. COR-86 widened
 * COR-25 in place, and that reaches `kind` through this call for free.
 *
 * Use this for a venue ABOUT TO BE CREATED, where the incoming name and
 * address are what the row will store. To re-derive an existing row, use
 * {@link rederiveVenueKind}, which reads the row's own columns.
 *
 * @param name the venue label as persisted / about to be persisted.
 * @param options.address the row's `address` column, when the caller has it.
 *   COR-25 uses it to CORROBORATE: a name that reads as an address but whose
 *   `address` column names a different street is not treated as an address
 *   (`123 Main St` vs `123 Unrelated Pkwy`). Omitting it (or passing `null`)
 *   leaves the name-shape signature authoritative, which is the correct
 *   behaviour when the caller genuinely has no address — never a shortcut to
 *   skip threading one that is in scope.
 *
 * Empty / whitespace-only names fall through to `'resolved'` — they're rare
 * (the persister won't create a Location at all when `venueName` is empty)
 * and the alternative of a fourth `'unknown'` enum value would force every
 * downstream consumer to handle a state that shouldn't exist on disk.
 */
export function classifyVenueKind(
  name: string,
  options: { unresolvedBrand?: boolean; address?: string | null } = {},
): VenueKindLabel {
  if (options.unresolvedBrand) return 'unresolved-brand';
  if (!name) return 'resolved';
  if (isAddressLikeVenueName(name, options.address ?? null)) return 'unresolved-address';
  if (isPlaceholderVenueName(name)) return 'unresolved-placeholder';
  return 'resolved';
}

/** The stored columns {@link rederiveVenueKind} reads. */
export interface StoredVenueKindInputs {
  /** The row's `name` — as stored, or as this same write is about to store it. */
  readonly name: string;
  /** The row's `address` — as stored, or as this same write is about to store it. */
  readonly address: string | null;
  /** The row's current `kind`. Consulted only for the sticky brand verdict. */
  readonly kind: VenueKindLabel;
}

/**
 * API-1717 — re-derive an EXISTING row's `kind` from the row's own columns.
 *
 * Two properties make this different from {@link classifyVenueKind}, and both
 * are load-bearing:
 *
 * **It reads the STORED name and address, never a string that merely matched
 * the row.** COR-25's `nameIsAddress` (admin) and the placement scorer (via
 * `rescoreEventsForLocation`) both judge the stored columns, so `kind` must
 * too or the three disagree. The Eventbrite-Apify persister used to
 * re-classify the FEED's venue name on every tick. It matches rows by Google
 * place id and by its own ingest key — precisely the arms that exist because
 * the stored name DIFFERS from the feed name — so every venue an operator had
 * renamed from `101 N Tryon St` to `Bank of America Corporate Center` was
 * condemned back to `unresolved-address` on the next run. Measured on the Neon
 * `production` branch on 2026-09-10: 22 renamed Eventbrite venues held that
 * stale verdict, hiding their events from Discover and TOP PICKS.
 *
 * **`unresolved-brand` is sticky.** It records a SOURCE-level signal (a
 * transformer's `unresolvedBrand` flag), which no name or address can
 * re-derive, so a re-derivation must never erase it. There are 0 such rows in
 * CLT today; the rule exists so that stays a data question rather than
 * becoming a code path that silently rewrites them.
 *
 * **`unresolved-placeholder` is NOT sticky** (COR-127). Unlike brand, it is a
 * verdict on the NAME, so the name re-derives it: renaming `TBA` to the real
 * venue heals the row to `resolved`, and renaming a venue to `To Be Announced`
 * condemns it. Keeping it would leave a remediated venue off Discover and TOP
 * PICKS, with nothing in admin to say why.
 *
 * Pure and deterministic, like COR-25.
 */
export function rederiveVenueKind(stored: StoredVenueKindInputs): VenueKindLabel {
  return classifyVenueKind(stored.name, {
    address: stored.address,
    unresolvedBrand: stored.kind === 'unresolved-brand',
  });
}

/**
 * PIP-87 — map a {@link VenueKindLabel} to the Prisma client's
 * identifier-safe spelling, for a model-API write.
 *
 * Typed as a string union rather than db-core's generated `VenueKind` because
 * db-core depends on this package; importing its generated types here would
 * be a cycle. Prisma's structural type check on the write payload still
 * catches a typo. A label outside the map (untyped input) falls back to
 * `'resolved'`, as it always has.
 */
export function venueKindToPrisma(label: VenueKindLabel): VenueKindPrismaValue {
  return VENUE_KIND_PRISMA_IDENTIFIERS[label] ?? 'resolved';
}
